import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import Image from 'next/legacy/image';

import BuildingCarousel from './BuildingCarousel';
import { flattenStrapiBulkDataItems } from '../../lib/flattenStrapiBulkDataItems';
import PreconstructionTableSection from './Preconstruction/PreconstructionTableSection';
import TableSection from './TableSection';
import PreconstructionAboutSection from './Preconstruction/PreconstructionAboutSection';
import AboutSection from './AboutSection';
import BuildingAmenities from './BuildingAmenities';
import RequestInfo from '../RequestInfo';
import AvailableCondos from './AvailableCondos/AvailableCondos';
import GuidesSection from './GuidesSection';
import BuildingFloorplansSection from './BuildingFloorplansSection';
import FinancingSection from './FinancingSection';
import BuildingStats from './BuildingStats/BuildingStats';
import LineBetweenSections from '../LineBetweenSections';
import RecentSales from '../RecentSales';
import NearbyCondos from './NearbyCondos';
import { BuildingPageContextInterface } from '../../apolloClient/types/Building';
import ErrorBoundary from '../ErrorBoundary';

import { BuildingPageContext } from '../../pages/[urlParam]/[urlBuildParam]';
import { PropertyType } from 'components/UnitsFilters/types';
import { Metadata } from 'components/Metadata';
import { getStrapiMedia } from 'lib/getStrapiMedia';
import ContentGroupTracker from 'src/gtm/ContentGroupTracker';
import { useRouter } from 'next/router';
import { usePPC } from 'components/PPCProvider';
import { useVisibleSection } from '../../hooks/useVisibleSection';
import { PageLayout } from 'components/layouts/PageLayout';
import { RelatedArticlesSection } from 'components/RelatedArticlesSection';
import { BuildingHeader } from './BuildingPageHeader';
import BuildingGalleryPopupHeaderV2 from 'componentsV2/layoutsV2/HeaderV2/GalleryPopupHeaderV2/BuildingGalleryPopupHeaderV2';
import { processImages } from 'lib/getImages';
import AvailableCondosV2 from 'componentsV2/BuldingV2/AvailableCondosV2/AvailableCondosV2';
import { Theme, useMediaQuery } from '@mui/material';
import { useExperts } from '../../hooks/useExperts';
import BuildingFloorplansSectionV2 from 'componentsV2/BuildingFloorplansSectionV2';

const BuildingPage: React.FC = () => {
  const { isPPC } = usePPC();
  const router = useRouter();

  const { building, blogs, neighborhood, url, favorite } = useContext(
    BuildingPageContext
  ) as BuildingPageContextInterface;

  const { experts } = useExperts();

  const {
    slug,
    name,
    images,
    isPreconstruction,
    videoUrl,
    lat,
    lon,
    floorplans,
  } = building;

  const [availableCondosPropertyType, setAvailableCondosPropertyType] =
    useState<PropertyType>('sale');

  const rentAndBuyRef = useRef<HTMLElement>(null);
  const aboutRef = useRef<HTMLElement>(null);
  const buildingAmenitiesRef = useRef<HTMLElement>(null);
  const AvailableCondosRef = useRef<any>(null);
  const FloorplansRef = useRef<HTMLElement>(null);
  const BuildingStatsRef = useRef<HTMLElement>(null);
  const EdgewaterCondosRef = useRef<HTMLElement>(null);
  const GuidesSectionRef = useRef<HTMLElement>(null);
  const RequestInfoRef = useRef<HTMLElement>(null);

  const buildingNameRef = useRef<HTMLHeadingElement>(null);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const linksForHeader = useMemo(
    () => [
      {
        id: 'BuildingAmenities',
        name: 'Building Amenities',
        nameMobile: 'Amenities',
        section: 'BuildingAmenities',
        ref: buildingAmenitiesRef,
        ignoreOffset: false,
      },
      {
        id: 'AvailableCondos',
        name: 'For Sale and Rent',
        nameMobile: 'For Sale and Rent',
        section: 'AvailableCondos',
        ref: AvailableCondosRef,
        ignoreOffset: true,
      },
      {
        id: 'Floorplans',
        name: 'Floorplans',
        nameMobile: 'Floorplans',
        section: 'Floorplans',
        ref: FloorplansRef,
        ignoreOffset: true,
      },
      {
        id: 'BuildingStats',
        name: 'Building Stats',
        nameMobile: 'Stats',
        section: 'BuildingStats',
        ref: BuildingStatsRef,
        ignoreOffset: true,
      },
      {
        id: 'RequestInfo',
        name: 'Request Info',
        nameMobile: 'Request Info',
        section: 'RequestInfo',
        ref: RequestInfoRef,
        ignoreOffset: false,
      },
    ],
    []
  );

  const linksForPreconstructionHeader = useMemo(
    () => [
      {
        id: 'BuildingAmenities',
        name: 'Building Amenities',
        nameMobile: 'Amenities',
        section: 'BuildingAmenities',
        ref: buildingAmenitiesRef,
        ignoreOffset: false,
      },
      {
        id: 'RequestInfo',
        name: 'Request Info',
        nameMobile: 'Request Info',
        section: 'RequestInfo',
        ref: RequestInfoRef,
        ignoreOffset: true,
      },
      {
        id: 'AvailableCondos',
        name: 'For Sale and Rent',
        nameMobile: 'For Sale and Rent',
        section: 'AvailableCondos',
        ref: AvailableCondosRef,
        ignoreOffset: true,
      },
      {
        id: 'Floorplans',
        name: 'Floorplans',
        nameMobile: 'Floorplans',
        section: 'Floorplans',
        ref: FloorplansRef,
        ignoreOffset: true,
      },
    ],
    []
  );

  const [isIconPPC, setIconIsPPC] = useState(false);

  const isBuildingPagePpc = router.query.att;
  function PPC() {
    if (isBuildingPagePpc && +isBuildingPagePpc >= 0) {
      setIconIsPPC(true);
    }
  }

  useEffect(() => {
    if (isBuildingPagePpc && +isBuildingPagePpc >= 0) {
      setIconIsPPC(true);
    }
  }, [isBuildingPagePpc]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  const flattenedImages = flattenStrapiBulkDataItems(images.data);
  const processedImages = processImages(flattenedImages, building.name);
  const cityName = neighborhood?.city?.data?.attributes?.name;

  const title = `${building.name} Condos for Sale and Rent in ${neighborhood.name} - ${cityName} | CondoBlackBook`;
  const description = `Explore and search ${building.name}, ${building.primaryAddress}, with real-time sale and rental availability, photos, videos, floor plans, condo search, recent sales and latest news.`;

  const sectionRefs = [
    { id: 'BuildingAmenities', ref: buildingAmenitiesRef },
    { id: 'AvailableCondos', ref: AvailableCondosRef },
    { id: 'Floorplans', ref: FloorplansRef },
    { id: 'BuildingStats', ref: BuildingStatsRef },
    { id: 'RequestInfo', ref: RequestInfoRef },
  ];

  const visibleSection = useVisibleSection(sectionRefs);

  const breadcrumbsProps = {
    neighborhoodId: neighborhood.slug,
    neighborhoodName: neighborhood.name,
    buildingName: name,
    buildingId: slug,
    buildingPage: true,
    preConstructionPage: isPreconstruction,
  };

  return (
    <>
      <Metadata
        {...{
          title,
          image: getStrapiMedia(flattenedImages[0], 'small'),
          description,
          url,
        }}
      />
      <main>
        <PageLayout
          title={building.name}
          links={
            isPreconstruction ? linksForPreconstructionHeader : linksForHeader
          }
          sectionScrollRef={rentAndBuyRef}
          visibleSection={visibleSection}
          breadcrumbs={breadcrumbsProps}
          scrollPastRef={buildingNameRef}
          headerContent={
            <>
              <ContentGroupTracker pageGroup="buildings" />
              <BuildingHeader
                name={building.name}
                primaryAddress={building.primaryAddress}
                buildingNameRef={buildingNameRef}
                rentAndBuyRef={rentAndBuyRef}
                buildingId={building.id}
                favoriteBuildings={favorite}
              />
            </>
          }
        >
          <ErrorBoundary>
            <BuildingCarousel
              nameButton="Floor plans"
              images={processedImages}
              position={
                lon && lat
                  ? {
                      lng: lon,
                      lat: lat,
                    }
                  : undefined
              }
              renderHeader={(onClose, photoCounter) => (
                <BuildingGalleryPopupHeaderV2
                  onClose={onClose}
                  photoCounter={photoCounter}
                />
              )}
              tours={videoUrl
                ?.split(',')
                .map((url: string) => url.trim())
                .filter((url) => url?.length > 0)}
              carouselKey={building.id.toString()}
            />
          </ErrorBoundary>
          {isPreconstruction ? (
            <ErrorBoundary>
              <PreconstructionTableSection
                availableCondosCoordinates={
                  AvailableCondosRef?.current?.offsetTop
                }
                setAvailableCondosPropertyType={setAvailableCondosPropertyType}
                href="#RequestInfo"
              />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary>
              <TableSection
                availableCondosCoordinates={
                  AvailableCondosRef?.current?.offsetTop
                }
                setAvailableCondosPropertyType={setAvailableCondosPropertyType}
              />
            </ErrorBoundary>
          )}
          {isPreconstruction ? (
            <ErrorBoundary>
              <PreconstructionAboutSection
                href="#RequestInfo"
                experts={experts}
              />
            </ErrorBoundary>
          ) : (
            <section
              ref={aboutRef}
              className="container relative pb-6 md:pb-0 section lg:pt-2 lg:pb-0 text-m"
            >
              <ErrorBoundary>
                <AboutSection href="#BuildingStats" />
              </ErrorBoundary>
            </section>
          )}
          <section
            ref={buildingAmenitiesRef}
            className="container relative section"
          >
            <div
              id="BuildingAmenities"
              className="absolute -top-28 lg:-top-24"
            />
            <ErrorBoundary>
              <BuildingAmenities />
            </ErrorBoundary>
          </section>
          {isPreconstruction && (
            <section
              ref={RequestInfoRef}
              className="relative px-0 pt-6 mt-10 xl:px-32 text-m bg-beige-light"
            >
              <div id="RequestInfo" className="absolute -top-20" />
              <ErrorBoundary>
                <RequestInfo experts={experts} />
              </ErrorBoundary>
            </section>
          )}
          <React.Fragment>
            <section
              ref={AvailableCondosRef}
              className="container relative pt-2 pb-8 lg:pb-[48px] section text-m"
            >
              <div
                id="AvailableCondos"
                className="absolute -top-14 md:-top-12"
              />
              <ErrorBoundary>
                {/* show/hide control is on AvailableCondosV2 and AvailableCondos main wrappers classes (lg:hidden)*/}
                {isDesktop ? (
                  <AvailableCondos
                    isIconPPC={isIconPPC}
                    isPPC={isPPC}
                    initialPropertyType={availableCondosPropertyType}
                  />
                ) : (
                  <AvailableCondosV2
                    isIconPPC={isIconPPC}
                    isPPC={isPPC}
                    initialPropertyType={availableCondosPropertyType}
                  />
                )}
              </ErrorBoundary>
            </section>
          </React.Fragment>
          <section ref={GuidesSectionRef} className="relative text-14-26-0.3">
            <Image
              layout="fill"
              src="/images/guides-background-min.jpg"
              className="absolute w-full h-full"
              alt=""
            />
            <div id="GuidesSection" className="absolute -top-32 md:-top-24" />
            <GuidesSection />
          </section>
          <section
            ref={FloorplansRef}
            className={`relative py-10 section container text-m ${
              isMobile ? 'px-0' : ''
            } `}
          >
            <div
              id="Floorplans"
              className="absolute top-[10px] md:-top-14 lg:-top-12"
            />
            <ErrorBoundary>
              {isMobile ? (
                <BuildingFloorplansSectionV2
                  floorplans={floorplans}
                  position={
                    lon && lat
                      ? {
                          lng: lon,
                          lat: lat,
                        }
                      : undefined
                  }
                  images={flattenedImages}
                />
              ) : (
                <BuildingFloorplansSection
                  floorplans={floorplans}
                  position={
                    lon && lat
                      ? {
                          lng: lon,
                          lat: lat,
                        }
                      : undefined
                  }
                  images={flattenedImages}
                />
              )}
            </ErrorBoundary>
          </section>
          {isPreconstruction ? null : (
            <React.Fragment>
              <ErrorBoundary>
                <FinancingSection
                  buildingName={name}
                  lowestDownPayment={building.lowestDownPayment}
                  className="mb-4 md:mt-[60px] md:mb-[76px]"
                />
              </ErrorBoundary>
              <section
                ref={BuildingStatsRef}
                className="container relative pt-2 mt-4 section lg:pt-2 md:pb-0 text-m"
              >
                <div
                  id="BuildingStats"
                  className="absolute top-[-60px] md:-top-10"
                />
                <ErrorBoundary>
                  <BuildingStats
                    href="#BuildingRecentSales"
                    showButton={classnames({
                      block: Number(building.unitsCount) > 0,
                      hidden: Number(building.unitsCount) === 0,
                    })}
                  />
                </ErrorBoundary>
                <LineBetweenSections hideLine={'md:hidden'} />
                <div className="relative">
                  <div id="BuildingRecentSales" className="absolute -top-20" />
                  <ErrorBoundary>
                    <RecentSales name={name} urlBuildParam={slug} />
                  </ErrorBoundary>
                </div>
              </section>
              <section
                ref={RequestInfoRef}
                className="relative px-0 pt-6 mb-6 xl:px-32 text-m bg-beige-light"
              >
                <div id="RequestInfo" className="absolute top-[-70px]" />
                <ErrorBoundary>
                  <RequestInfo experts={experts} />
                </ErrorBoundary>
              </section>
            </React.Fragment>
          )}
          <section ref={EdgewaterCondosRef} className="relative">
            <div id="EdgewaterCondos" className="absolute -top-28" />
            <ErrorBoundary>
              <NearbyCondos />
            </ErrorBoundary>
          </section>
          <RelatedArticlesSection blogs={blogs} />
        </PageLayout>
      </main>
    </>
  );
};

export default BuildingPage;
